// Springs, in the two numbers a person can actually picture.
//
// Apple stopped describing motion as mass, stiffness and damping and switched
// to two parameters, and those are what this takes:
//
//   damping   1.0 settles without bouncing. Below 1.0 it overshoots.
//   response  seconds to reach the target. Not a duration — a spring has no
//             duration. It stops when it has stopped moving.
//
// The reason for a spring rather than a CSS transition is interruption. A
// transition runs from where it was told to start to where it was told to end,
// and a finger landing on it half way through can only wait for it to finish.
// A spring carries on from wherever the thing is right now, at the speed it is
// already travelling, so a drawer can be caught in flight and thrown back the
// other way without the motion ever cutting.

const REST_DISTANCE = 0.4   // px
const REST_VELOCITY  = 4    // px/s

export class Spring {
  constructor({ from, to, velocity = 0, damping = 1, response = 0.4, onUpdate, onRest }) {
    this.value = from
    this.velocity = velocity
    this.target = to
    this.damping = damping
    this.response = response
    this.onUpdate = onUpdate
    this.onRest = onRest
    this.last = null
    this.tick = this.tick.bind(this)
    this.frame = requestAnimationFrame(this.tick)
  }

  // Re-aiming mid-flight keeps the position and the speed it already had.
  // Replacing one animation with another instead is what makes a reversal feel
  // like it hits a wall.
  retarget(to, velocity = this.velocity) {
    this.target = to
    this.velocity = velocity
    if (!this.frame) {
      this.last = null
      this.frame = requestAnimationFrame(this.tick)
    }
  }

  stop() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
  }

  tick(now) {
    const elapsed = this.last === null ? 0 : Math.min((now - this.last) / 1000, 0.064)
    this.last = now
    this.frame = requestAnimationFrame(this.tick)

    // Fixed sub-steps. A dropped frame should change how many steps the spring
    // takes, never where it ends up.
    const omega = (2 * Math.PI) / this.response
    const stiffness = omega * omega
    const friction = 2 * this.damping * omega

    let left = elapsed
    while (left > 0) {
      const dt = Math.min(left, 1 / 240)
      const acceleration = -stiffness * (this.value - this.target) - friction * this.velocity
      this.velocity += acceleration * dt
      this.value += this.velocity * dt
      left -= dt
    }

    if (Math.abs(this.value - this.target) < REST_DISTANCE && Math.abs(this.velocity) < REST_VELOCITY) {
      this.value = this.target
      this.velocity = 0
      this.stop()
      this.onUpdate?.(this.value)
      this.onRest?.()
      return
    }

    this.onUpdate?.(this.value)
  }
}

// Where a flick would come to rest if it were left to run out — the same
// exponential decay the browser uses for scroll momentum. Landing on the edge
// nearest the release point throws the throw away; landing on the edge nearest
// this is what lets a small movement move a big thing.
export function project(velocity, decelerationRate = 0.998) {
  return (velocity / 1000) * decelerationRate / (1 - decelerationRate)
}

// Past the end of the track, follow the finger less and less. A hard stop reads
// as frozen. Resistance reads as "you have reached the end of it".
export function rubberband(overshoot, dimension, constant = 0.55) {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot))
}

// Release velocity taken from the last several moves rather than the last one,
// which on its own lands on whatever jitter the final event happened to carry.
export class VelocityTracker {
  constructor(span = 100) {
    this.span = span
    this.samples = []
  }

  reset() {
    this.samples = []
  }

  push(value, time) {
    const at = time ?? performance.now()
    this.samples.push({ value, time: at })
    while (this.samples.length > 2 && at - this.samples[0].time > this.span) this.samples.shift()
  }

  get velocity() {
    if (this.samples.length < 2) return 0

    const first = this.samples[0]
    const last = this.samples[this.samples.length - 1]
    const seconds = (last.time - first.time) / 1000
    return seconds > 0 ? (last.value - first.value) / seconds : 0
  }
}

export function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}
